import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from './firebase';

/**
 * One school calendar, assembled from the records the platform already holds.
 *
 * There is no calendar collection and nothing is synchronised into one. Every source below is
 * already staff-readable, so the calendar can simply ask them for the visible date range and merge
 * the answers in memory. That is the whole design, and it is the reason this is a small file:
 *
 *   - Nothing can drift. A materialised calendar would need a trigger per source, a delete path per
 *     source, and a reconciliation task when those inevitably miss one -- which is the shape of bug
 *     this codebase has spent real time unpicking in the substitution balances.
 *   - No new rules. The fan-out rows for notices exist because a rule had to narrow a read down to
 *     one family; nothing here needs narrowing, because every source is open to all staff already.
 *   - The calendar cannot show something that is not true, because it is not a copy.
 *
 * The cost is one query per source per view change. They are small, scoped by date, and the modules
 * already issue exactly these queries individually.
 */

// What each source is called and how it is drawn. Colours are literal rather than semantic tokens:
// their job is to be told apart from each other, not to signal a status, and a palette sweep that
// replaced them with --color-success and friends would quietly make two sources the same colour.
export const CAL_SOURCES = {
  school: { label: 'School calendar', colour: '#123A4A' },
  ole: { label: 'OLE activity', colour: '#2C6E8A' },
  invigilation: { label: 'Invigilation', colour: '#7A5410' },
  patrol: { label: 'Patrol', colour: '#C0A062' },
  substitution: { label: 'Cover lesson', colour: '#B3402A' },
  absence: { label: 'Absence', colour: '#606D74' },
  tutorial: { label: 'Tutorial', colour: '#3E7C59' },
  booking: { label: 'Booking', colour: '#8A6FA0' },
  notice: { label: 'Notice date', colour: '#245B3C' },
};

export const iso = (d) => d.toLocaleDateString('en-CA');
export const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
export const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

// Times arrive as HH:MM:SS from some sources and HH:MM from others. Seconds are never meaningful
// on a school timetable and they push the useful part off the end of a narrow row.
const hhmm = (t) => String(t || '').slice(0, 5);
const span = (a, b) => [hhmm(a), hhmm(b)].filter(Boolean).join('–');

const rows = async (schoolId, col, ...clauses) => {
  const snap = await getDocs(query(collection(db, 'schools', schoolId, col), ...clauses));
  const out = [];
  snap.forEach(d => out.push({ uid: d.id, ...d.data() }));
  return out;
};

/**
 * Everything happening between two ISO dates, inclusive.
 *
 * `mine` narrows to the caller. It is applied here rather than by each screen because "is this
 * mine" means something different in every source -- an invigilation names you in an array, a cover
 * lesson names you as one of two people, a booking names you as the person who made it -- and
 * spreading that across the callers is how the two views would come to disagree.
 */
// `t` is optional and defaults to returning the English it is given, so this module still runs --
// and still tests -- with no dictionary at all. It builds a finished entry, title and detail
// included, so the words have to be resolved here rather than in the component that shows them.
export const fetchCalendar = async ({ schoolId, from, to, email, initial, sources, t = (_k, _v, fb) => fb }) => {
  const want = (id) => !sources || sources.includes(id);
  const me = (email || '').toLowerCase();
  const jobs = [];

  // Patrol assignments store a session id, and the label lives in the module's settings. Read once
  // per fetch: without it the calendar shows people a row that says "s_msdjvp18_y77pxx".
  let patrolSessions = [];
  if (want('patrol')) {
    try {
      const cfg = await getDoc(doc(db, 'schools', schoolId, 'metadata', 'patrol_settings'));
      patrolSessions = (cfg.exists() ? cfg.data().sessions : []) || [];
    } catch (e) { console.error('calendar: patrol settings', e); }
  }
  const patrolLabel = (id) => {
    const hit = patrolSessions.find(x => x.id === id);
    if (!hit) return '';
    return [hit.label || hit.id, span(hit.startTime, hit.endTime)].filter(Boolean).join(' · ');
  };

  const push = (id, fn) => { if (want(id)) jobs.push(fn().catch((e) => { console.error(`calendar: ${id}`, e); return []; })); };

  push('school', async () => (await rows(schoolId, 'calendar_events',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `sch-${r.uid}`, source: 'school', date: r.date,
      title: r.description || r.type || t('agenda.titles.school', {}, 'School calendar'),
      detail: r.cycleDay ? t('agenda.cycleDay', { day: r.cycleDay }, `Cycle day ${r.cycleDay}`) : '',
      // Not "mine" -- a holiday belongs to nobody in particular, and badging it MINE made the badge
      // meaningless everywhere else. Flagged as whole-school instead, which is what keeps it in the
      // Mine view: a teacher filtering to their own day still needs to know the school is shut.
      mine: false, wholeSchool: true,
      ref: { col: 'calendar_events', uid: r.uid },
    })));

  push('ole', async () => (await rows(schoolId, 'ole_session',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `ole-${r.uid}`, source: 'ole', date: r.date,
      title: r.titleEn || r.titleZh || t('agenda.titles.ole', {}, 'Activity'),
      detail: [r.venueEn || r.venueZh, span(r.timeStart, r.timeEnd)].filter(Boolean).join(' · '),
      mine: false, activityId: r.activityId,
      ref: { col: 'ole_session', uid: r.uid },
    })));

  push('invigilation', async () => (await rows(schoolId, 'invigilation_session',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `inv-${r.uid}`, source: 'invigilation', date: r.date,
      title: r.subject || t('agenda.titles.invigilation', {}, 'Examination'),
      detail: [r.venue, span(r.startTime, r.endTime)].filter(Boolean).join(' · '),
      mine: (r.invigilatorEmails || []).includes(me) || (r.invigilators || []).includes(initial),
      people: r.invigilators || [],
      ref: { col: 'invigilation_session', uid: r.uid },
    })));

  push('patrol', async () => (await rows(schoolId, 'patrol_assignment',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `pat-${r.uid}`, source: 'patrol', date: r.date,
      title: patrolLabel(r.sessionId) || t('agenda.titles.patrol', {}, 'Patrol'), detail: '',
      mine: (r.staffEmails || []).includes(me) || (r.staff || []).includes(initial),
      people: r.staff || [],
      ref: { col: 'patrol_assignment', uid: r.uid },
    })));

  push('substitution', async () => (await rows(schoolId, 'lesson_substitutions',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `sub-${r.uid}`, source: 'substitution', date: r.date,
      title: [r.class, r.subj].filter(Boolean).join(' ') || t('agenda.titles.substitution', {}, 'Cover lesson'),
      detail: [r.periodCode, r.room, span(r.start_time, r.end_time),
        r.substituteStaffId ? `${r.substituteStaffId} covering` : '',
        r.absentStaffId ? `for ${r.absentStaffId}` : ''].filter(Boolean).join(' · '),
      mine: [r.substituteEmail, r.absentEmail].includes(me)
        || [r.substituteStaffId, r.absentStaffId].includes(initial),
      ref: { col: 'lesson_substitutions', uid: r.uid },
    })));

  push('absence', async () => (await rows(schoolId, 'staff_absences',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `abs-${r.uid}`, source: 'absence', date: r.date,
      title: t('agenda.staffAway', { staff: r.staffId || '' }, `${r.staffId || ''} away`).trim(),
      detail: r.remarks || '',
      mine: r.staffEmail === me || r.staffId === initial,
      ref: { col: 'staff_absences', uid: r.uid },
    })));

  push('tutorial', async () => (await rows(schoolId, 'tutorial_record',
    where('date', '>=', from), where('date', '<=', to)))
    .map(r => ({
      key: `tut-${r.uid}`, source: 'tutorial', date: r.date,
      title: r.subject || t('agenda.titles.tutorial', {}, 'Tutorial'),
      detail: [
        r.description,
        t('agenda.studentCount', { count: (r.students || []).length }, `${(r.students || []).length} student(s)`),
      ].filter(Boolean).join(' · '),
      mine: r.submittedBy === me,
      students: r.students || [],
      ref: { col: 'tutorial_record', uid: r.uid },
    })));

  // Bookings carry a range, so a booking that straddles the window has to be caught by its START
  // being at or before the end of it. Firestore allows range clauses on one field only, hence the
  // single-sided query and the second half of the test in memory.
  const bookings = async (col, label) => (await rows(schoolId, col, where('startDate', '<=', to)))
    .filter(r => (r.endDate || r.startDate) >= from)
    .map(r => ({
      key: `${col}-${r.uid}`, source: 'booking', date: r.startDate,
      endDate: r.endDate || r.startDate,
      title: t('agenda.bookingTitle', {
        kind: label,
        what: r.locationName || r.assetName || r.itemName || r.purpose || t('agenda.titles.booking', {}, 'Booking'),
      }, `${label}: ${r.locationName || r.assetName || r.itemName || r.purpose || 'Booking'}`),
      detail: [r.purpose, r.userId || r.bookedBy || ''].filter(Boolean).join(' · '),
      mine: (r.bookedByEmail || '').toLowerCase() === me,
      ref: { col, uid: r.uid },
    }));
  push('booking', () => bookings('location_bookings', t('agenda.bookingRoom', {}, 'Room')));
  push('booking', () => bookings('mdm_bookings', t('agenda.bookingDevice', {}, 'Device')));

  // Notices carry a list of dates rather than one, so a single notice can land on several days --
  // and an array cannot be range-filtered, so this is the one source read whole and narrowed here.
  // Acceptable while a school issues notices in the hundreds; if that stops being true the fix is a
  // firstEventDate field on the notice, not a wider query.
  push('notice', async () => (await rows(schoolId, 'parent_notice'))
    .flatMap(r => (r.eventDates || [])
      .filter(d => d >= from && d <= to)
      .map(d => ({
        key: `not-${r.uid}-${d}`, source: 'notice', date: d,
        title: r.title || t('agenda.titles.notice', {}, 'Notice'),
        detail: t('agenda.fromParentNotice', {}, 'From a parent notice'),
        mine: false, ref: { col: 'parent_notice', uid: r.uid },
      }))));

  const all = (await Promise.all(jobs)).flat();
  return all.sort((a, b) => String(a.date).localeCompare(String(b.date))
    || String(a.source).localeCompare(String(b.source))
    || String(a.title).localeCompare(String(b.title)));
};

/**
 * Who is on one entry. Fetched only when somebody opens it: a month of OLE sessions would otherwise
 * be one participant query per session before anything appeared on screen.
 */
export const fetchParticipants = async (schoolId, entry) => {
  if (entry.source === 'ole' && entry.activityId) {
    const list = await rows(schoolId, 'ole_participant', where('activityId', '==', entry.activityId));
    return list.map(r => ({
      sid: r.sid, name: r.studentName || r.name || r.sid,
      detail: [r.currentClass, r.role || r.position].filter(Boolean).join(' · '),
    }));
  }
  if (entry.source === 'tutorial' && (entry.students || []).length) {
    // In batches of 30: `in` takes no more than that, and a tutorial group can exceed it.
    const out = [];
    for (let i = 0; i < entry.students.length; i += 30) {
      const batch = entry.students.slice(i, i + 30);
      const list = await rows(schoolId, 'student_profiles', where('sid', 'in', batch));
      list.forEach(r => out.push({
        sid: r.sid,
        name: `${r.lastName || ''} ${r.firstName || ''}`.trim() || r.sid,
        detail: [r.currentClass, r.classNumber].filter(Boolean).join(' · '),
      }));
    }
    return out;
  }
  if ((entry.people || []).length) {
    // Staff initials, as the record stores them. Resolved by document id where the address is
    // known, and left as the initial where it is not -- shown as-is rather than dropped.
    return entry.people.map(p => ({ sid: p, name: p, detail: '' }));
  }
  return [];
};
